<template>
  <div class="activity-edit-container">
    <div class="page-header">
      <el-page-header @back="goBack" :content="isEdit ? '编辑活动' : '发布活动'"></el-page-header>
    </div>

    <el-card shadow="never" style="margin-top: 16px;">
      <el-form ref="activityForm" :model="formData" :rules="formRules" label-width="110px" size="medium">
        <el-row :gutter="24">
          <el-col :span="16">
            <el-form-item label="活动标题" prop="title">
              <el-input v-model="formData.title" placeholder="请输入活动标题" maxlength="200" show-word-limit></el-input>
            </el-form-item>
            <el-form-item label="活动副标题" prop="subtitle">
              <el-input v-model="formData.subtitle" placeholder="请输入活动副标题" maxlength="300" show-word-limit></el-input>
            </el-form-item>
            <el-form-item label="活动内容" prop="content">
              <el-input
                v-model="formData.content"
                type="textarea"
                :autosize="{ minRows: 18, maxRows: 40 }"
                placeholder="请输入活动内容"
                class="content-textarea"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="封面图片">
              <div class="cover-upload-area">
                <div class="cover-preview" v-if="formData.coverImage" @click="triggerCoverUpload">
                  <el-image :src="resolveUrl(formData.coverImage)" fit="cover" style="width: 100%; height: 100%;"></el-image>
                  <div class="cover-overlay">
                    <i class="el-icon-upload2"></i>
                    <span>更换封面</span>
                  </div>
                </div>
                <div class="cover-placeholder" v-else @click="triggerCoverUpload">
                  <i class="el-icon-plus"></i>
                  <span>上传封面</span>
                </div>
                <input type="file" ref="coverInput" style="display: none" accept="image/*" @change="handleCoverUpload" />
              </div>
            </el-form-item>
            <el-form-item label="活动分类" prop="category">
              <el-select v-model="formData.category" placeholder="请选择分类" style="width: 100%">
                <el-option v-for="item in categoryOptions" :key="item.key" :label="item.name" :value="item.key"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="活动状态" prop="status">
              <el-select v-model="formData.status" placeholder="请选择状态" style="width: 100%" @change="handleStatusChange">
                <el-option label="草稿" value="DRAFT"></el-option>
                <el-option label="发布" value="PUBLISHED"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="置顶展示">
              <el-switch v-model="formData.isTop" :disabled="formData.status !== 'PUBLISHED'"></el-switch>
            </el-form-item>
            <el-form-item label="活动时间">
              <el-date-picker
                v-model="activityTimeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                value-format="yyyy-MM-dd HH:mm:ss"
                style="width: 100%"
                @change="handleTimeRangeChange"
              ></el-date-picker>
            </el-form-item>
            <el-form-item label="活动地点">
              <el-input v-model="formData.activityLocation" placeholder="请输入活动地点" maxlength="300"></el-input>
            </el-form-item>
            <el-form-item label="活动链接">
              <el-input v-model="formData.activityLink" placeholder="请输入活动链接" maxlength="500"></el-input>
            </el-form-item>
            <el-form-item label="组织者">
              <el-input v-model="formData.organizer" placeholder="请输入组织者" maxlength="100"></el-input>
            </el-form-item>
            <el-form-item label="联系方式">
              <el-input v-model="formData.contactInfo" placeholder="请输入联系方式" maxlength="200"></el-input>
            </el-form-item>
            <el-form-item label="最大参与人数">
              <el-input-number v-model="formData.maxParticipants" :min="0" placeholder="不限" style="width: 100%"></el-input-number>
            </el-form-item>
            <el-form-item label="标签">
              <el-input v-model="formData.tags" placeholder="多个标签用逗号分隔" maxlength="500"></el-input>
            </el-form-item>
            <el-form-item label="备注">
              <el-input v-model="formData.remark" type="textarea" :rows="3" placeholder="备注信息" maxlength="500"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">活动图片</el-divider>
        <el-form-item label="图片管理">
          <div class="image-management">
            <div class="image-list" ref="imageList">
                <div class="image-item" v-for="(img, idx) in formData.images" :key="idx">
                  <div class="image-drag-handle"><i class="el-icon-rank"></i></div>
                  <el-image :src="resolveUrl(img.imageUrl)" fit="cover" class="image-thumb" :preview-src-list="formData.images.map(i => resolveUrl(i.imageUrl))"></el-image>
                  <div class="image-info">
                    <el-input v-model="img.caption" size="mini" placeholder="图片说明" maxlength="200"></el-input>
                  </div>
                  <div class="image-actions">
                    <el-button type="text" size="mini" @click="removeImage(idx)" style="color: #f56c6c;"><i class="el-icon-delete"></i></el-button>
                  </div>
                </div>
              <div class="image-add-btn" @click="triggerImageUpload">
                <i class="el-icon-plus"></i>
                <span>添加图片</span>
              </div>
              <input type="file" ref="imageInput" style="display: none" accept="image/*" multiple @change="handleImageUpload" />
            </div>
          </div>
        </el-form-item>

        <div class="form-footer">
          <el-button @click="goBack">取消</el-button>
          <el-button type="info" @click="handleSaveDraft" :loading="submitLoading" v-if="hasPermission('activity:community:save')">保存草稿</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitLoading">提交</el-button>
        </div>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { getActivityDetail, createActivity, updateActivity, uploadActivityImage } from '@/apis/activity/activity'
import { getDictDataByType } from '@/apis/system/dict'
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils'
import Sortable from 'sortablejs'

export default {
  name: 'CommunityEdit',
  data() {
    return {
      isEdit: false,
      loading: false,
      submitLoading: false,
      activityTimeRange: null,
      categoryOptions: [],
      formData: {
        id: null,
        title: '',
        subtitle: '',
        coverImage: '',
        category: '',
        status: 'DRAFT',
        isTop: false,
        publishTime: null,
        activityStartTime: null,
        activityEndTime: null,
        activityLocation: '',
        content: '',
        activityLink: '',
        organizer: '',
        contactInfo: '',
        maxParticipants: null,
        currentParticipants: 0,
        tags: '',
        remark: '',
        images: []
      },
      formRules: {
        title: [{ required: true, message: '请输入活动标题', trigger: 'blur' }],
        category: [{ required: true, message: '请选择活动分类', trigger: 'change' }],
        status: [{ required: true, message: '请选择活动状态', trigger: 'change' }]
      }
    }
  },
  created() {
    this.loadCategoryDict()
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      this.loadDetail(id)
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initSortable()
    })
  },
  methods: {
    hasPermission,
    resolveUrl(url) {
      return getFullUrl(url)
    },
    loadCategoryDict() {
      getDictDataByType('ACTIVITY_CATEGORY').then(res => {
        this.categoryOptions = res || []
      }).catch(e => {
        console.error('加载活动分类字典失败:', e)
        this.categoryOptions = []
      })
    },
    goBack() {
      this.$router.push('/activity/community')
    },
    loadDetail(id) {
      this.loading = true
      getActivityDetail(id).then(res => {
        if (res) {
          const data = res
          this.formData = {
            ...data,
            images: data.images || [],
            isTop: !!data.isTop
          }
          if (data.activityStartTime && data.activityEndTime) {
            this.activityTimeRange = [data.activityStartTime, data.activityEndTime]
          }
        }
      }).catch(e => {
        console.error('加载活动详情失败:', e)
      }).finally(() => {
        this.loading = false
      })
    },
    handleTimeRangeChange(val) {
      if (val) {
        this.formData.activityStartTime = val[0]
        this.formData.activityEndTime = val[1]
      } else {
        this.formData.activityStartTime = null
        this.formData.activityEndTime = null
      }
    },
    handleStatusChange(val) {
      // 状态变为非发布时，自动取消置顶
      if (val !== 'PUBLISHED') {
        this.formData.isTop = false
      }
    },
    triggerCoverUpload() {
      this.$refs.coverInput.click()
    },
    async handleCoverUpload(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await uploadActivityImage(formData)
        if (res) {
          this.formData.coverImage = res
          this.$message.success('封面上传成功')
        }
      } catch (err) {
        this.$message.error('封面上传失败')
      }
      e.target.value = ''
    },
    triggerImageUpload() {
      this.$refs.imageInput.click()
    },
    async handleImageUpload(e) {
      const files = Array.from(e.target.files)
      for (const file of files) {
        try {
          const formData = new FormData()
          formData.append('file', file)
          const res = await uploadActivityImage(formData)
          if (res) {
            this.formData.images.push({
              imageUrl: res,
              caption: '',
              sortOrder: this.formData.images.length
            })
          }
        } catch (err) {
          this.$message.error(`图片 ${file.name} 上传失败`)
        }
      }
      e.target.value = ''
    },
    removeImage(idx) {
      this.formData.images.splice(idx, 1)
    },
    initSortable() {
      if (this.$refs.imageList) {
        Sortable.create(this.$refs.imageList, {
          animation: 150,
          handle: '.image-drag-handle',
          onEnd: ({ oldIndex, newIndex }) => {
            if (oldIndex !== undefined && newIndex !== undefined && oldIndex !== newIndex) {
              const item = this.formData.images.splice(oldIndex, 1)[0]
              this.formData.images.splice(newIndex, 0, item)
              this.onImageOrderChange()
            }
          }
        })
      }
    },
    onImageOrderChange() {
      this.formData.images.forEach((img, idx) => {
        img.sortOrder = idx
      })
    },
    collectFormData() {
      return { ...this.formData }
    },
    handleSaveDraft() {
      this.$refs.activityForm.validate(valid => {
        if (!valid) return
        const data = this.collectFormData()
        data.status = 'DRAFT'
        this.submitLoading = true
        const action = this.isEdit ? updateActivity(data) : createActivity(data)
        action.then(() => {
          this.$message.success('保存草稿成功')
          this.goBack()
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    handleSubmit() {
      this.$refs.activityForm.validate(valid => {
        if (!valid) return
        const data = this.collectFormData()
        this.submitLoading = true
        const action = this.isEdit ? updateActivity(data) : createActivity(data)
        action.then(() => {
          this.$message.success(this.isEdit ? '更新成功' : '发布成功')
          this.goBack()
        }).finally(() => {
          this.submitLoading = false
        })
      })
    }
  }
}
</script>

<style scoped>
.activity-edit-container {
  padding: 20px;
}

.page-header {
  background: #fff;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.content-textarea >>> textarea {
  font-size: 14px;
  line-height: 1.8;
  resize: vertical;
}

.cover-upload-area {
  width: 100%;
}

.cover-preview {
  width: 100%;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  border: 1px solid #ebeef5;
}

.cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  opacity: 0;
  transition: opacity 0.3s;
}

.cover-preview:hover .cover-overlay {
  opacity: 1;
}

.cover-placeholder {
  width: 100%;
  height: 160px;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  cursor: pointer;
  transition: border-color 0.3s;
}

.cover-placeholder:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.cover-placeholder i {
  font-size: 32px;
  margin-bottom: 8px;
}

.image-management {
  width: 100%;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.image-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  width: calc(50% - 6px);
  min-width: 280px;
}

.image-drag-handle {
  cursor: move;
  color: #c0c4cc;
  font-size: 16px;
  flex-shrink: 0;
}

.image-drag-handle:hover {
  color: #909399;
}

.image-thumb {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  flex-shrink: 0;
  border: 1px solid #ebeef5;
}

.image-info {
  flex: 1;
  min-width: 0;
}

.image-actions {
  flex-shrink: 0;
}

.image-add-btn {
  width: calc(50% - 6px);
  min-width: 280px;
  height: 80px;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  cursor: pointer;
  transition: border-color 0.3s;
}

.image-add-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  margin-top: 20px;
}
</style>
